import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import {green, grey, red, yellow} from '@material-ui/core/colors';
import {makeStyles} from '@material-ui/core/styles';
import {
  Add,
  ArrowBackIosOutlined,
  ArrowDownward,
  Check,
  ChevronLeft,
  ChevronRight,
  Clear,
  Close,
  Description,
  Edit,
  FilterList,
  FirstPage,
  Info,
  LastPage,
  Search
} from '@material-ui/icons';
import MaterialTable from 'material-table';
import moment from 'moment';
import {PropTypes} from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Notary} from '../classes/Notary';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {
  approveApplication,
  getApplications
} from '../script/application-service';
import {getNotaryApplications} from '../script/person-service';
import {addNotifications} from '../store/appActions';
import cleanseNotaryData from '../utils/cleanseNotaryFormData';
import {
  NOTARY_APPLICATION_STATUS_APPROVED,
  NOTARY_APPLICATION_STATUS_NEW,
  NOTARY_APPLICATION_STATUS_UPDATED,
  NOTARY_APPLICATION_STATUS_UPDATE_PERSON,
  USER_TYPE_ADMIN
} from '../utils/constants';
import NotaryForm from './NotaryForm';

const useStyles = makeStyles(() => ({
  root: {marginTop: '3%'},
  backButton: {marginTop: '20px', color: 'white'},
  APPROVED: {backgroundColor: green[300], color: grey[50], width: '12em'},
  NEW: {backgroundColor: red[300], color: grey[50], width: '12em'},
  UPDATED: {backgroundColor: yellow[300], color: grey[900], width: '12em'},
  UPDATE_PERSON: {backgroundColor: yellow[300], color: grey[900], width: '12em'}
}));

/* eslint-disable max-lines-per-function */
/**
 * Notary Registration view for submitting signed document.
 *
 * @param {P} props - The property data.
 * @return {*} - The rendered string.
 */
function NotaryApplications(props) {
  const classes = useStyles();
  const addApplicationNotifications = (notifications) =>
    props.dispatch(addNotifications(notifications));
  const [applications, setApplications] = useState([]);
  const [userId, setUserId] = useState(null);
  const [isAdmin] = useState(props.user.type === USER_TYPE_ADMIN);
  const [openDetails, setOpenDetails] = useState(false);
  const [showApplicationSubmitButton, setShowApplicationSubmitButton] =
    useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [showUserActionRow, setShowUserActionRow] = useState(false);
  const [hidePatentDate, setHidePatentDate] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [applicationData, setApplicationData] = useState(false);

  const tableIcons = {
    Search: Search,
    Filter: FilterList,
    PreviousPage: ChevronLeft,
    NextPage: ChevronRight,
    FirstPage: FirstPage,
    LastPage: LastPage,
    Add: Add,
    Edit: Edit,
    Check: Check,
    Close: Close,
    SortArrow: ArrowDownward,
    ResetSearch: Clear
  };

  const tableLocalization = {
    toolbar: {
      searchTooltip: locale.tableSearchTooltip,
      searchPlaceholder: locale.tableSearchPlaceholder
    },
    header: {
      actions: locale.tableHeaderActions
    },
    body: {
      emptyDataSourceMessage: locale.tableEmptyDataSourceMessage
    },
    pagination: {
      labelRowsPerPage: locale.tableLabelRowsPerPage,
      labelDisplayedRows: locale.tableLabelDisplayedRows,
      labelRowsSelect: locale.tableLabelRowsSelect,
      firstTooltip: locale.tableFirstTooltip,
      previousTooltip: locale.tablePreviousTooltip,
      nextTooltip: locale.tableNextTooltip,
      lastTooltip: locale.tableLastTooltip
    }
  };

  useEffect(() => {
    loadData();
  });
  const loadData = () => {
    if (props.user.id !== userId) {
      if (isAdmin) {
        getNotaryApplications().then((response) => {
          setApplications(response);
          setUserId(props.user.id);
        });
      } else {
        getApplications(props.user.id).then((response) => {
          setApplications(response);
          setUserId(props.user.id);
        });
      }
    }
  };

  /**
   * Handles the notary application data.
   *
   * @param {Object} rowData - The given row data.
   */
  const handleApplicationData = (rowData: Object) => {
    const notaryData = new Notary(
      rowData.gender,
      rowData.firstName,
      rowData.name,
      rowData.birthDate,
      rowData.patentDate,
      rowData.uid,
      rowData.company,
      rowData.street,
      rowData.streetNumber,
      rowData.addition,
      rowData.postOffice,
      rowData.postcode,
      rowData.location,
      rowData.careOf,
      rowData.email,
      rowData.phoneNumber,
      rowData.faxNumber,
      null,
      rowData.id,
      rowData.status,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      rowData.mobileNumber
    );
    setApplicationData(notaryData);
  };

  /**
   * Handles the submit of the application.
   *
   * @param {Object} applicationData - The notary application data from submit action.
   */
  const handleApplicationSubmit = (applicationData: Object) => {
    if (applicationData) {
      cleanseNotaryData(applicationData);
      applicationData.sendMail = false;
      applicationData.stateNotaryId = props.user.id;
      applicationData.status = NOTARY_APPLICATION_STATUS_APPROVED;
      approveApplication(applicationData).then((response) => {
        if (response.approved) {
          addApplicationNotifications([
            new Notification(
              locale.approveApplicationCompletedNotification,
              'success',
              6000
            )
          ]);
          getApplications(props.user.id).then((response) => {
            setOpenDetails(false);
            setApplications(response);
            setUserId(props.user.id);
          });
        } else {
          addApplicationNotifications([
            new Notification(
              locale.approveApplicationErrorNotification,
              'error',
              6000
            )
          ]);
        }
      });
    }
  };

  const setupDetailDialog = (row: Object) => {
    if (row.status === NOTARY_APPLICATION_STATUS_UPDATED) {
      setupDialog(false, true, true, true, false);
    } else if (row.status === NOTARY_APPLICATION_STATUS_UPDATE_PERSON) {
      setupDialog(true, false, true, true, true);
    } else if (row.status === NOTARY_APPLICATION_STATUS_NEW) {
      setupDialog(true, false, false, false, false);
    } else if (row.status === NOTARY_APPLICATION_STATUS_APPROVED) {
      setupDialog(true, true, true, false, false);
    }
  };

  const setupDialog = (
    df: Boolean,
    sdb: Boolean,
    suar: Boolean,
    sasb: Boolean,
    hpd: Boolean
  ) => {
    setDisableFields(df);
    setShowDownloadButton(sdb);
    setShowUserActionRow(suar);
    setShowApplicationSubmitButton(sasb);
    setHidePatentDate(hpd);
  };

  const customSortNullsLast = (a: String | null, b: String | null) => {
    if (!a) {
      return -1;
    }
    if (!b) {
      return +1;
    }

    return a.localeCompare(b);
  };

  return (
    <div id='notary-applications'>
      {!openDetails && (
        <div id='noatry-applications-table' className={classes.root}>
          <MaterialTable
            icons={tableIcons}
            title={locale.notaryApplicationsTitle}
            localization={tableLocalization}
            columns={[
              {
                title: locale.createDate,
                field: 'createDate',
                type: 'date',
                render: (rowData) =>
                  rowData.createDate &&
                  moment(rowData.createDate).format(locale.localizedDateFormat),
                customSort: (a, b) =>
                  customSortNullsLast(a.createDate, b.createDate)
              },
              {title: 'id', field: 'id', hidden: true},
              {
                title: locale.name,
                field: 'name',
                customSort: (a, b) => a.name.localeCompare(b.name)
              },
              {
                title: locale.firstName,
                field: 'firstName',
                customSort: (a, b) => a.firstName.localeCompare(b.firstName)
              },
              {
                title: locale.status,
                field: 'status',
                // eslint-disable-next-line react/display-name
                render: (rowData) => (
                  <Chip
                    variant='outlined'
                    size='small'
                    className={classes[rowData.status]}
                    label={locale.getString(
                      'applicationStatus' + rowData.status
                    )}
                  />
                )
              },
              {
                title: locale.approveDate,
                field: 'approveDate',
                type: 'date',
                render: (rowData) =>
                  rowData.approveDate &&
                  moment(rowData.approveDate).format(
                    locale.localizedDateFormat
                  ),
                customSort: (a, b) =>
                  customSortNullsLast(a.approveDate, b.approveDate)
              }
            ]}
            actions={[
              {
                icon: isAdmin ? Info : Description,
                tooltip: isAdmin ? locale.details : locale.editApplication,
                onClick: (event, rowData) => {
                  {
                    setupDetailDialog(rowData);
                    setOpenDetails(true);
                    handleApplicationData(rowData);
                  }
                }
              }
            ]}
            data={applications
              .sort((a, b) => customSortNullsLast(a.createDate, b.createDate))
              .reverse()}
            options={{
              sorting: true,
              actionsColumnIndex: -1,
              showTitle: true,
              search: true
            }}
          />
        </div>
      )}
      {openDetails && (
        <div id='notary-applications-detail'>
          <Button
            className={classes.backButton}
            size='small'
            onClick={() => {
              setOpenDetails(false);
            }}
            startIcon={<ArrowBackIosOutlined />}
          >
            {locale.back}
          </Button>
          <NotaryForm
            data={applicationData}
            showApplicationSubmitButton={showApplicationSubmitButton}
            showDownloadButton={showDownloadButton}
            showUserActionRow={showUserActionRow}
            disableFields={disableFields}
            handleSubmit={handleApplicationSubmit}
            hidePatentDate={hidePatentDate}
            headline={locale.editApplicationTitle}
            saveButtonLabel={locale.notaryApplicationSubmitButton}
          />
        </div>
      )}
    </div>
  );
}

NotaryApplications.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired
};

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    dispatch: dispatch
  };
}

/**
 * Map the state to the properties.
 *
 * @param {Object} state - The state from store.
 * @return {Object} - The state.
 */
const mapStateToProps = (state: Object) => {
  return {user: state.user};
};

export default connect(mapStateToProps, mapDispatchToProps)(NotaryApplications);
